import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, MapPin, CalendarDays, Users, Ticket, Download, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, fileUrl } from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { LGAS } from "@/lib/data";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SocialShare from "@/components/SocialShare";

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [reg, setReg] = useState({ name: "", email: "", phone: "", lga: "" });
  const [submitting, setSubmitting] = useState(false);
  const [ticket, setTicket] = useState(null);

  const load = () => api.get("/events").then((r) => setEvents(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!reg.name || !reg.phone || !reg.lga) return toast.error("Please fill required fields");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/events/${active.id}/register`, reg);
      toast.success("You're registered! Your ticket is ready.");
      setActive(null);
      setReg({ name: "", email: "", phone: "", lga: "" });
      load();
      setTicket(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const downloadTicket = async () => {
    try {
      const res = await fetch(fileUrl(ticket.ticket_path));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `nasarawa-ticket-${ticket.ticket_id}.png`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download ticket"); }
  };

  return (
    <main className="pt-28 pb-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="overline-label mb-3">What's Happening</div>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight flex items-center gap-3"><CalendarDays className="w-8 h-8 text-[#E6197E]" /> Events</h1>

        {loading ? <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#E6197E]" /></div> :
          events.length === 0 ? <div className="py-24 text-center text-zinc-500">No events scheduled yet.</div> : (
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {events.map((ev, i) => (
              <motion.div key={ev.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.05, 0.4) }}
                className="rounded-2xl border border-white/10 bg-[#131519] overflow-hidden flex flex-col">
                {ev.image && <div className="aspect-video bg-zinc-800 overflow-hidden"><img src={mediaUrl(ev.image)} alt="" loading="lazy" className="w-full h-full object-cover" /></div>}
                <div className="p-5 flex flex-col flex-1">
                  <span className={`self-start text-[10px] px-2 py-0.5 rounded-full font-semibold mb-2 ${ev.scope === "national" ? "bg-[#E6197E]/20 text-[#E6197E]" : "bg-amber-600/20 text-amber-400"}`}>{ev.scope === "national" ? "National" : ev.lga}</span>
                  <h3 className="font-heading font-semibold text-lg leading-tight">{ev.title}</h3>
                  <p className="text-sm text-zinc-400 mt-2 line-clamp-2 flex-1">{ev.description}</p>
                  <div className="mt-4 space-y-1.5 text-xs text-zinc-400">
                    <div className="flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5" /> {ev.date}</div>
                    <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> {ev.location}</div>
                    <div className="flex items-center gap-2"><Users className="w-3.5 h-3.5" /> {ev.registration_count} registered</div>
                  </div>
                  <Button onClick={() => setActive(ev)} className="mt-4 bg-[#E6197E] hover:bg-[#C4136B] rounded-full" data-testid="event-register-button"><Ticket className="w-4 h-4 mr-2" /> Register</Button>
                  <div className="mt-2">
                    <SocialShare url={`${window.location.origin}/events`} text={`Join "${ev.title}" — a Nasarawa Expo event on ${ev.date} at ${ev.location}.`} label="Share event" testid="event-share" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="bg-[#131519] border-white/10">
          <DialogHeader><DialogTitle className="font-heading">Register for {active?.title}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div><Label>Full Name *</Label><Input value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} className="mt-2 bg-[#0B0C0E] border-white/10" data-testid="event-reg-name" /></div>
            <div><Label>Phone *</Label><Input value={reg.phone} onChange={(e) => setReg({ ...reg, phone: e.target.value })} className="mt-2 bg-[#0B0C0E] border-white/10" data-testid="event-reg-phone" /></div>
            <div><Label>Email</Label><Input type="email" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} className="mt-2 bg-[#0B0C0E] border-white/10" /></div>
            <div><Label>LGA *</Label>
              <Select value={reg.lga} onValueChange={(v) => setReg({ ...reg, lga: v })}>
                <SelectTrigger className="mt-2 bg-[#0B0C0E] border-white/10" data-testid="event-reg-lga"><SelectValue placeholder="Select LGA" /></SelectTrigger>
                <SelectContent>{LGAS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-[#E6197E] hover:bg-[#C4136B] rounded-full" data-testid="event-reg-submit">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm Registration"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!ticket} onOpenChange={() => setTicket(null)}>
        <DialogContent className="bg-[#131519] border-white/10 max-w-xl">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-400" /> You're in! Here's your ticket</DialogTitle></DialogHeader>
          {ticket && (
            <div data-testid="ticket-confirmation">
              {ticket.ticket_path
                ? <img src={fileUrl(ticket.ticket_path)} alt="Event ticket" className="w-full rounded-xl border border-white/10" data-testid="ticket-image" />
                : <div className="text-sm text-zinc-400">Ticket generated. ID: {ticket.ticket_id}</div>}
              <div className="mt-3 text-xs text-zinc-400 text-center">Ticket ID <span className="font-mono-accent text-zinc-200">{ticket.ticket_id}</span> · Scan the QR at the gate to verify attendance.</div>
              {ticket.ticket_path && (
                <Button onClick={downloadTicket} className="w-full mt-4 bg-[#E6197E] hover:bg-[#C4136B] rounded-full font-semibold" data-testid="ticket-download-button">
                  <Download className="w-4 h-4 mr-2" /> Download Ticket
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
